<template>
  <woot-modal :show.sync="show" :on-close="onClose" class="outbound-visible" size="medium">
    <div class="h-auto flex flex-col docal-modal-body">
      <woot-modal-header
        :header-title="$t('DOCAL_UNSIGNED_DOCUMENTS_MODAL.TITLE')"
        :header-content="$t('DOCAL_UNSIGNED_DOCUMENTS_MODAL.UNSIGNED_DESCRIPTION')"
      />
      <div v-if="isLoading" class="docal-loading">
        <spinner />
        <p>{{ $t('DOCAL_UNSIGNED_DOCUMENTS_MODAL.LOADING') }}</p>
      </div>
      <div v-else class="flex flex-col h-full">
        <div class="bg-white flex-1 docal-table-wrapper">
          <ve-table
            :fixed-header="true"
            :columns="columns"
            :table-data="tableData"
            :border-around="false"
            :sort-option="sortOption"
            :max-height="400"
            row-key-field-name="rowId"
          />
          <empty-state
            v-if="showEmptyState"
            :title="$t('DOCAL_UNSIGNED_DOCUMENTS_MODAL.NO_DOCUMENTS_FOUND')"
          />
        </div>
      </div>
      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <button class="button clear" @click.prevent="onClose">
          {{ $t('DOCAL_UNSIGNED_DOCUMENTS_MODAL.CLOSE') }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Spinner from 'shared/components/Spinner.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import alertMixin from 'shared/mixins/alertMixin';
import DocalAPI from '../../../../api/docal';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export default {
  name: 'DocalUnsignedModal',
  components: {
    Spinner,
    EmptyState,
    VeTable,
  },
  mixins: [rtlMixin, alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      documents: [],
      isLoading: false,
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortChange: params => {
          this.sortConfig = params;
          this.applySorting();
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    showEmptyState() {
      return !this.isLoading && this.documents.length === 0;
    },
    tableData() {
      if (this.isLoading) return [];
      return this.documents.map((doc, index) => ({
        rowId: index,
        file_name: doc.file_name,
        page_count: doc.page_count != null ? doc.page_count : '',
        client_opened: doc.client_opened ? this.$t('DOCAL_UNSIGNED_DOCUMENTS_MODAL.YES') : this.$t('DOCAL_UNSIGNED_DOCUMENTS_MODAL.NO'),
        signing_url: doc.signing_url || '',
        created_at_formatted: doc.created_at_formatted || '',
      }));
    },
    columns() {
      return [
        {
          field: 'file_name',
          key: 'file_name',
          sortBy: this.sortConfig.file_name || '',
          title: this.$t('DOCAL_TABLE.FILE_NAME'),
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => {
            const name = row.file_name;
            if (!name) return '';
            return (
              <span title={name} class="file-name-cell">{name}</span>
            );
          },
        },
        {
          field: 'page_count',
          key: 'page_count',
          title: this.$t('DOCAL_TABLE.PAGES'),
          width: 70,
          align: 'center',
        },
        {
          field: 'client_opened',
          key: 'client_opened',
          title: this.$t('DOCAL_TABLE.OPENED'),
          width: 80,
          align: 'center',
        },
        {
          field: 'signing_url',
          key: 'signing_url',
          title: this.$t('DOCAL_TABLE.SIGNING_URL'),
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => {
            const url = row.signing_url;
            if (!url) return '';
            return (
              <span class="signing-url-wrapper">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={url}
                  class="signing-url-link"
                >
                  {url}
                </a>
                <woot-button
                  variant="clear"
                  size="tiny"
                  color-scheme="secondary"
                  icon="clipboard"
                  class-names="copy-button"
                  title={this.$t('CONTACT_PANEL.COPY_URL')}
                  onClick={e => {
                    this.onCopy(e, url);
                  }}
                />
              </span>
            );
          },
        },
        {
          field: 'created_at_formatted',
          key: 'created_at_formatted',
          sortBy: this.sortConfig.created_at_formatted || '',
          title: this.$t('DOCAL_TABLE.ADDED_DATE'),
          width: 160,
          align: this.isRTLView ? 'right' : 'left',
        },
      ];
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.fetchUnsignedDocuments();
      } else {
        this.reset();
      }
    },
  },
  methods: {
    async onCopy(e, url) {
      e.preventDefault();
      e.stopPropagation();
      await copyTextToClipboard(url);
      this.showAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    async fetchUnsignedDocuments() {
      this.isLoading = true;
      this.documents = [];
      try {
        const response = await DocalAPI.getUnsignedDocumentsList(this.accountId, this.contactId);
        if (response.status !== 200) {
          const errorMsg = 'Failed to load documents. Please try again.';
          console.error('Docal unsigned documents error:', errorMsg, response.data);
          return;
        }
        this.documents = response.data.pending_documents || [];
      } catch (error) {
        const errorMsg = 'Failed to load documents. Please try again.';
        console.error('Docal unsigned documents error:', errorMsg, error);
      } finally {
        this.isLoading = false;
      }
    },
    applySorting() {
      const key = Object.keys(this.sortConfig).find(
        k => this.sortConfig[k] !== ''
      );
      if (!key) return;
      const direction = this.sortConfig[key];
      this.documents.sort((a, b) => {
        const valA = a[key];
        const valB = b[key];
        if (typeof valA === 'string') {
          return direction === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        return direction === 'asc' ? valA - valB : valB - valA;
      });
    },
    onClose() {
      this.$emit('close');
    },
    reset() {
      this.documents = [];
      this.sortConfig = {};
    },
  },
};
</script>

<style lang="scss" scoped>
.docal-modal-body {
  overflow: visible !important;
  padding: var(--space-normal);
  padding-bottom: 2rem;
  padding-left: 1rem;
}

.docal-modal-body :deep(.woot-modal-header) {
  padding-left: 1rem !important;
}

.docal-modal-body .px-8 {
  padding-left: 1rem !important;
}
.docal-modal-body .px-8 {
  padding-bottom: 2rem !important;
}

.docal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-large);
}

.error-message {
  color: var(--r-500);
}

::v-deep td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.signing-url-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
::v-deep .signing-url-link {
  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--w-500);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.ve-table {
  @apply bg-white dark:bg-slate-900;
  padding-left: 1rem;

  &::v-deep {
    .ve-table-container {
      overflow-y: hidden;
    }
  }
}
::v-deep(.ve-table-sort) {
  line-height: normal;
}
</style>
